/**
 * ShortPaymentLinkPage — public landing for short payment URLs (`/p/:slug`).
 *
 * Resolves the slug via the public API and redirects the browser to the Stripe
 * Checkout URL. Renders friendly error states for cancelled/expired/unknown links.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

const STATES = {
  loading:   { title: 'Préparation de votre paiement…', message: 'Vous allez être redirigé vers la page de paiement sécurisée.' },
  cancelled: { title: 'Lien annulé',  message: 'Ce lien de paiement a été annulé. Merci de contacter l\u2019émetteur.' },
  expired:   { title: 'Lien expiré',  message: 'Ce lien de paiement a expiré. Merci de demander un nouveau lien.' },
  notfound:  { title: 'Lien introuvable', message: 'Ce lien de paiement n\u2019existe pas ou a été supprimé.' },
  paid:      { title: 'Déjà réglé', message: 'Ce paiement a déjà été effectué. Merci !' },
  error:     { title: 'Une erreur est survenue', message: 'Impossible de charger ce lien. Veuillez réessayer plus tard.' },
};

export default function ShortPaymentLinkPage() {
  const { slug } = useParams();
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/custom-payment-links/short/${slug}`);
        if (cancelled) return;
        if (!data) { setState('notfound'); return; }
        if (data.status === 'cancelled') { setState('cancelled'); return; }
        if (data.status === 'expired') { setState('expired'); return; }
        if (data.status === 'paid') { setState('paid'); return; }
        if (data.stripe_url) {
          window.location.replace(data.stripe_url);
          return;
        }
        setState('error');
      } catch (e) {
        if (cancelled) return;
        setState(e.response?.status === 404 ? 'notfound' : 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const view = STATES[state] || STATES.error;

  return (
    <div
      style={{
        margin: 0,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#0B0B0B',
        color: '#E5E5E5',
        fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif',
      }}
      data-testid="payment-redirect-page"
    >
      <div style={{
        maxWidth: 480, width: '100%', textAlign: 'center', background: '#1A1A1A',
        border: '1px solid #2A2A2A', borderRadius: 12, padding: '40px 28px',
      }}>
        {state === 'loading' && (
          <div
            style={{
              width: 36, height: 36, margin: '0 auto 18px',
              border: '3px solid #2A2A2A', borderTopColor: '#C9A24D', borderRadius: '50%',
              animation: 'cpl-spin 0.8s linear infinite',
            }}
          />
        )}
        <h1 style={{ margin: '0 0 12px', fontSize: 22, color: '#C9A24D' }} data-testid="payment-redirect-title">
          {view.title}
        </h1>
        <p style={{ margin: 0, color: '#B0B0B0', lineHeight: 1.5 }}>{view.message}</p>
        {state !== 'loading' && (
          <a
            href="https://y-go.fr"
            style={{
              color: '#C9A24D', textDecoration: 'none', display: 'inline-block',
              marginTop: 24, padding: '10px 20px', border: '1px solid #C9A24D', borderRadius: 8,
            }}
          >
            Retour au site
          </a>
        )}
      </div>
      <style>{`@keyframes cpl-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
